import React, { useEffect, useRef, useState } from "react";
import { getSessionApi, getGuestStore } from "../../../utils/demoApi";
import { resolveSessionKind } from "../sessionKind";

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const DEMO_REGISTER_FORM_ID = "demo-register-form";

/**
 * Reusable registration form for BOTH live-session products. Renders inputs,
 * custom questions and captcha (when configured). The submit button lives
 * outside this component (in the sticky page footer) and triggers submit via
 * the HTML `form` attr.
 *
 * Props:
 *  - session, slug — required
 *  - kind — "demo" | "webinar"; picks the endpoint, the storage namespace and
 *    every visible word.
 *  - onRegistered(slug) — called after a successful POST
 *  - onSubmittingChange(boolean) — pushed up so the footer button can
 *    reflect submitting state and disable itself.
 */
const DemoRegisterForm = ({ session, slug, kind = "demo", onRegistered, onSubmittingChange }) => {
  const vocab = resolveSessionKind(kind);
  const api = getSessionApi(vocab.kind);
  const guestStore = getGuestStore(vocab.kind);
  const isGuest = session?.registrationMode === "guest";
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [consent, setConsent] = useState(false);
  const [customAnswers, setCustomAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  useEffect(() => { onSubmittingChange?.(submitting); }, [submitting, onSubmittingChange]);

  // Lazily load + render Turnstile when captcha is required.
  useEffect(() => {
    if (!session?.captcha?.siteKey || !captchaRef.current) return undefined;
    const ensureScript = () => new Promise((resolve) => {
      if (window.turnstile) return resolve();
      const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve(), { once: true });
        return;
      }
      const s = document.createElement("script");
      s.src = TURNSTILE_SRC;
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      document.head.appendChild(s);
    });
    let mounted = true;
    ensureScript().then(() => {
      if (!mounted || !window.turnstile || !captchaRef.current) return;
      turnstileWidgetId.current = window.turnstile.render(captchaRef.current, {
        sitekey: session.captcha.siteKey,
        callback: (token) => setCaptchaToken(token),
        "error-callback": () => setCaptchaToken(""),
        "expired-callback": () => setCaptchaToken(""),
      });
    });
    return () => {
      mounted = false;
      try {
        if (window.turnstile && turnstileWidgetId.current) {
          window.turnstile.remove(turnstileWidgetId.current);
        }
      } catch { /* ignore */ }
    };
  }, [session?.captcha?.siteKey]);

  const setAnswer = (key, value) =>
    setCustomAnswers((p) => ({ ...p, [key]: value }));

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError("");

    if (!name.trim()) return setError("Please enter your name.");

    // Guest mode collects the name only — skip all other validation.
    if (!isGuest) {
      if (!mobile.trim() || mobile.replace(/\D/g, "").length < 8) {
        return setError("Please enter a valid mobile number.");
      }
      if (!consent) return setError("Please agree to the consent before continuing.");

      for (const q of session.customQuestions || []) {
        if (q.required) {
          const v = customAnswers[q.key];
          if (v === undefined || v === null || v === "" || v === false) {
            return setError(`Please answer: ${q.label}`);
          }
        }
      }
      if (session.captcha?.siteKey && !captchaToken) {
        return setError("Please complete the captcha challenge.");
      }
    }

    setSubmitting(true);
    try {
      const body = isGuest
        ? { name: name.trim() }
        : {
            name: name.trim(),
            mobile: mobile.trim(),
            email: session.collectEmail ? email.trim() : undefined,
            city: session.collectCity ? city.trim() : undefined,
            consent: true,
            customAnswers,
            captchaToken: captchaToken || undefined,
          };
      const { data } = await api.register(slug, body);

      if (data?.guestToken) {
        guestStore.save(slug, {
          token: data.guestToken,
          demoLeadId: data.demoLeadId,
          savedAt: Date.now(),
        });
      }
      onRegistered?.(slug);
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed. Please try again.");
      try {
        if (window.turnstile && turnstileWidgetId.current) {
          window.turnstile.reset(turnstileWidgetId.current);
        }
      } catch { /* ignore */ }
      setCaptchaToken("");
    }
    setSubmitting(false);
  };

  return (
    <div className="demo-split__view">
      <div className="demo-split__form-header">
        <div className="demo-split__form-header-text">
          <h2 className="demo-split__title">
            {isGuest ? "Join as guest" : vocab.copy.registerTitle}
          </h2>
          <p className="demo-split__desc">
            {isGuest
              ? `Just enter your name to join the live ${vocab.noun} — no sign-up needed.`
              : vocab.copy.registerSubtitle}
          </p>
        </div>
      </div>

      <form
        id={DEMO_REGISTER_FORM_ID}
        className="demo-public__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="demo-public__field">
          <label htmlFor="dr-name">
            Full name *{session.certificate?.enabled ? " — as it should appear on your certificate" : ""}
          </label>
          <input id="dr-name" type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </div>
        {!isGuest && (
        <div className="demo-public__field">
          <label htmlFor="dr-mobile">Mobile number *</label>
          <input id="dr-mobile" type="tel" autoComplete="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="+91 9876543210" required />
        </div>
        )}
        {!isGuest && session.collectEmail && (
          <div className="demo-public__field">
            <label htmlFor="dr-email">Email</label>
            <input id="dr-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        )}
        {!isGuest && session.collectCity && (
          <div className="demo-public__field">
            <label htmlFor="dr-city">City</label>
            <input id="dr-city" type="text" autoComplete="address-level2" value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
        )}

        {!isGuest && (session.customQuestions || []).map((q) => (
          <div key={q.key} className="demo-public__field">
            <label htmlFor={`dr-q-${q.key}`}>
              {q.label}{q.required ? " *" : ""}
            </label>
            {q.type === "textarea" ? (
              <textarea
                id={`dr-q-${q.key}`}
                rows={3}
                value={customAnswers[q.key] || ""}
                onChange={(e) => setAnswer(q.key, e.target.value)}
              />
            ) : q.type === "select" ? (
              <select
                id={`dr-q-${q.key}`}
                value={customAnswers[q.key] || ""}
                onChange={(e) => setAnswer(q.key, e.target.value)}
              >
                <option value="">— Select —</option>
                {(q.options || []).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : q.type === "checkbox" ? (
              <label className="demo-public__consent">
                <input
                  id={`dr-q-${q.key}`}
                  type="checkbox"
                  checked={!!customAnswers[q.key]}
                  onChange={(e) => setAnswer(q.key, e.target.checked)}
                />
                <span>{q.label}</span>
              </label>
            ) : (
              <input
                id={`dr-q-${q.key}`}
                type="text"
                value={customAnswers[q.key] || ""}
                onChange={(e) => setAnswer(q.key, e.target.value)}
              />
            )}
          </div>
        ))}

        {!isGuest && (
          <label className="demo-public__consent">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{session.consentText}</span>
          </label>
        )}

        {!isGuest && session.captcha?.siteKey && (
          <div className="demo-public__captcha" ref={captchaRef} />
        )}

        {error && <div className="demo-public__error">{error}</div>}
      </form>
    </div>
  );
};

export default DemoRegisterForm;
